<template>
  <div v-loading="loading" class="operations-view">
    <div class="operations-head">
      <div>
        <p class="eyebrow">PRODUCTION SAFETY</p>
        <h2>运行状态与告警处置</h2>
        <p class="muted">
          最近刷新 {{ formatTime(status.refreshedAt) }}
          <el-tag v-if="status.stale" size="mini" type="warning">快照已过期</el-tag>
          <el-tag v-else size="mini" type="success">快照正常</el-tag>
        </p>
      </div>
      <el-button icon="el-icon-refresh" @click="load">刷新快照</el-button>
    </div>

    <el-row :gutter="16">
      <el-col
        v-for="card in backlogCards"
        :key="card.code"
        :xs="12"
        :sm="8"
        :lg="4"
      >
        <div class="metric-card">
          <span>{{ card.label }}</span>
          <strong>{{ card.value }}</strong>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="panel-row">
      <el-col :xs="24" :lg="16">
        <section class="ops-panel">
          <div class="panel-title">
            <h3>阈值告警</h3>
            <span>只显示安全聚合值，不包含消息或供应商标识</span>
          </div>
          <el-table :data="status.alerts || []" empty-text="暂无告警配置">
            <el-table-column label="状态" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="row.active ? 'danger' : 'success'" size="mini">
                  {{ row.active ? '告警' : '正常' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="code" label="告警代码" min-width="170" />
            <el-table-column prop="value" label="当前值" width="90" />
            <el-table-column prop="threshold" label="阈值" width="80" />
            <el-table-column label="处置手册" min-width="220">
              <template slot-scope="{ row }">
                <code>{{ row.runbookPath }}</code>
              </template>
            </el-table-column>
            <el-table-column v-if="status.alertDrillsEnabled" label="UAT 演练" width="100">
              <template slot-scope="{ row }">
                <el-button
                  size="mini"
                  type="text"
                  :disabled="drilling"
                  @click="confirmDrill(drillType(row.code))"
                >
                  触发
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </section>
      </el-col>
      <el-col :xs="24" :lg="8">
        <section class="ops-panel retention-panel">
          <div class="panel-title">
            <h3>数据留存任务</h3>
            <span>
              受保护数据 {{ retention.protectedDataDays || 0 }} 天 /
              聚合 {{ retention.aggregateDays || 0 }} 天 /
              审计 {{ retention.auditDays || 0 }} 天
            </span>
          </div>
          <dl>
            <div><dt>最近结果</dt><dd>{{ retentionLabel }}</dd></div>
            <div><dt>调度开关</dt><dd>{{ retention.enabled ? '已启用' : '未启用' }}</dd></div>
            <div><dt>最近运行</dt><dd>{{ formatTime(retention.lastRunAt) }}</dd></div>
            <div><dt>最近变更</dt><dd>{{ retention.lastChanged || 0 }}</dd></div>
            <div><dt>批次大小</dt><dd>{{ retention.batchSize || 0 }}</dd></div>
            <div><dt>处置手册</dt><dd><code>ops/runbooks/retention-cleanup.md</code></dd></div>
          </dl>
        </section>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getOperationalStatus, runAlertDrill } from '../api/admin'

const labels = {
  outbox_backlog: 'Outbox 积压',
  callback_backlog: '回调积压',
  unmatched_callbacks: '未匹配回调',
  callback_timeouts: '回调超时',
  provider_failures: '供应商失败',
  authentication_failures: '认证失败'
}

export default {
  name: 'OperationsView',
  data () {
    return {
      loading: false,
      drilling: false,
      status: {
        backlogs: {},
        alerts: [],
        retention: {}
      }
    }
  },
  computed: {
    backlogCards () {
      return Object.keys(labels).map(code => ({
        code,
        label: labels[code],
        value: this.status.backlogs[code] || 0
      }))
    },
    retention () {
      return this.status.retention || {}
    },
    retentionLabel () {
      const labels = { success: '成功', failed: '失败', not_run: '尚未运行' }
      return labels[this.retention.result] || '未知'
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        this.status = await getOperationalStatus()
      } finally {
        this.loading = false
      }
    },
    drillType (code) {
      const aliases = {
        provider_failures: 'provider_failure',
        callback_timeouts: 'callback_timeout',
        unmatched_callbacks: 'unmatched_callback'
      }
      return aliases[code] || code
    },
    async confirmDrill (type) {
      await this.$confirm(
        `确认在当前非生产环境触发 ${type} 合成告警？操作会写入审计。`,
        '告警演练确认',
        { type: 'warning', confirmButtonText: '确认触发' }
      )
      this.drilling = true
      try {
        const result = await runAlertDrill(type)
        this.$message.success(`演练已触发，审计号 ${result.auditId}`)
        await this.load()
      } finally {
        this.drilling = false
      }
    },
    formatTime (value) {
      return value ? new Date(`${value}Z`).toLocaleString() : '—'
    }
  }
}
</script>

<style lang="scss" scoped>
.operations-view {
  .operations-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  h2, h3 { margin: 0; color: #10243e; }
  .muted { margin: 8px 0 0; color: #718096; }

  .metric-card {
    min-height: 92px;
    margin-bottom: 16px;
    padding: 18px;
    border: 1px solid #e5eaf1;
    border-radius: 10px;
    background: #fff;

    span { display: block; color: #718096; font-size: 13px; }
    strong { display: block; margin-top: 9px; color: #122b49; font-size: 28px; }
  }

  .panel-row { margin-top: 4px; }
  .ops-panel {
    padding: 20px;
    border: 1px solid #e5eaf1;
    border-radius: 10px;
    background: #fff;
  }

  .panel-title {
    margin-bottom: 14px;
    span { display: block; margin-top: 5px; color: #8492a6; font-size: 12px; }
  }

  code {
    color: #35516f;
    font-family: Consolas, monospace;
    font-size: 12px;
    word-break: break-all;
  }

  dl { margin: 0; }
  dl div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 13px 0;
    border-bottom: 1px solid #edf1f5;
  }
  dt { color: #718096; }
  dd { margin: 0; color: #162f4d; text-align: right; }
}
</style>
